// Lexique médical français pour la correction locale de l'active recall (aucun appel IA).
//
// POURQUOI. Les notes d'active recall sont prises de mémoire, vite, pour soi : télégraphiques et abrégées. Un
// correcteur qui compare les mots bruts compte « manque » une bonne réponse écrite « HTA », « 7 mmol/L » ou
// « ttt ». Il ne se contente pas de décourager : il fabrique des lacunes qui n'existent pas, et ces fausses
// lacunes vont piloter les révisions, les flashcards et le mini-cours. Le lexique est donc la pièce maîtresse,
// pas un accessoire.
//
// FORME. Un groupe = une liste de façons d'écrire la MÊME chose. La première entrée sert de clé ; toutes les
// autres s'y ramènent. Les accents, la casse et les traits d'union sont retirés à la construction de l'index,
// on peut donc écrire ici en français normal.
//
// AMBIGUÏTÉ ASSUMÉE. Certains sigles en recouvrent plusieurs (EI = effet indésirable ou endocardite
// infectieuse ; PC = perte de connaissance ou périmètre crânien). Ils appartiennent alors à PLUSIEURS groupes,
// et deux termes se correspondent dès qu'ils partagent un groupe. On préfère un rapprochement de trop à une
// fausse lacune : un point compté juste à tort sera revu de toute façon, une bonne réponse comptée fausse
// décourage et pollue le profil.

export const GROUPES = [
    // --- cardiovasculaire
    ['hypertension artérielle', 'HTA', 'hypertendu', 'hypertensive', 'hypertensif'],
    ['infarctus du myocarde', 'IDM', 'infarctus'],
    ['syndrome coronarien aigu', 'SCA', 'SCA ST+', 'SCA ST-', 'syndrome coronaire aigu'],
    ['angor', 'angine de poitrine', 'angor stable', 'angor instable'],
    ['arrêt cardio-respiratoire', 'ACR', 'arrêt cardiaque', 'arrêt circulatoire'],
    ['fibrillation atriale', 'FA', 'AC/FA', 'ACFA', 'fibrillation auriculaire'],
    ['flutter atrial', 'flutter'],
    ['tachycardie ventriculaire', 'TV'],
    ['fibrillation ventriculaire', 'FV'],
    ['extrasystole ventriculaire', 'ESV'],
    ['bloc atrio-ventriculaire', 'BAV', 'bloc auriculo-ventriculaire'],
    ['bloc de branche gauche', 'BBG'],
    ['bloc de branche droit', 'BBD'],
    ['fraction d\'éjection du ventricule gauche', 'FEVG', 'fraction d\'éjection'],
    ['insuffisance cardiaque', 'IC', 'ICC', 'insuffisance cardiaque chronique', 'défaillance cardiaque'],
    ['œdème aigu du poumon', 'OAP', 'oedeme aigu pulmonaire', 'œdème pulmonaire'],
    ['échographie transthoracique', 'ETT', 'échocardiographie', 'échocardiographie transthoracique'],
    ['échographie transœsophagienne', 'ETO'],
    ['électrocardiogramme', 'ECG'],
    ['artériopathie oblitérante des membres inférieurs', 'AOMI', 'artériopathie des membres inférieurs'],
    ['accident vasculaire cérébral', 'AVC', 'ictus'],
    ['accident ischémique transitoire', 'AIT'],
    ['thrombose veineuse profonde', 'TVP', 'phlébite'],
    ['embolie pulmonaire', 'EP'],
    ['maladie thromboembolique veineuse', 'MTEV', 'maladie veineuse thromboembolique'],
    ['hypertension artérielle pulmonaire', 'HTAP'],
    ['rétrécissement aortique', 'RAo', 'sténose aortique'],
    ['insuffisance aortique', 'IAo'],
    ['rétrécissement mitral', 'RM'],
    ['insuffisance mitrale', 'IM'],
    ['cardiomyopathie hypertrophique', 'CMH'],
    ['cardiomyopathie dilatée', 'CMD'],
    ['endocardite infectieuse', 'EI', 'endocardite'],
    ['pression artérielle', 'PA', 'TA', 'tension artérielle'],
    ['fréquence cardiaque', 'FC', 'pouls'],
    ['dissection aortique', 'dissection de l\'aorte'],
    ['anévrisme de l\'aorte abdominale', 'AAA', 'anévrysme de l\'aorte abdominale'],

    // --- pneumologie
    ['bronchopneumopathie chronique obstructive', 'BPCO'],
    ['épreuves fonctionnelles respiratoires', 'EFR', 'spirométrie'],
    ['volume expiratoire maximal seconde', 'VEMS'],
    ['capacité vitale forcée', 'CVF', 'CV'],
    ['syndrome de détresse respiratoire aiguë', 'SDRA'],
    ['pneumonie aiguë communautaire', 'PAC', 'pneumopathie aiguë communautaire', 'pneumopathie communautaire'],
    ['pneumopathie interstitielle diffuse', 'PID'],
    ['fréquence respiratoire', 'FR'],
    ['saturation pulsée en oxygène', 'SpO2', 'saturation', 'saturation en oxygène'],
    ['gaz du sang', 'GDS', 'gazométrie'],
    ['ventilation non invasive', 'VNI'],
    ['oxygénothérapie de longue durée', 'OLD'],
    ['dilatation des bronches', 'DDB', 'bronchectasies'],
    ['pneumothorax', 'PNO'],
    ['radiographie de thorax', 'RT', 'radio de thorax', 'radiographie thoracique', 'RP', 'radiographie pulmonaire'],
    ['syndrome d\'apnées obstructives du sommeil', 'SAOS', 'SAHOS', 'apnées du sommeil'],
    ['asthme aigu grave', 'AAG'],

    // --- imagerie et biologie courantes
    ['tomodensitométrie', 'TDM', 'scanner', 'scanographie'],
    ['imagerie par résonance magnétique', 'IRM'],
    ['numération formule sanguine', 'NFS', 'hémogramme'],
    ['protéine C réactive', 'CRP'],
    ['procalcitonine', 'PCT'],
    ['vitesse de sédimentation', 'VS'],
    ['ionogramme', 'iono', 'ionogramme sanguin'],
    ['bilan hépatique', 'BH'],
    ['examen cytobactériologique des urines', 'ECBU'],
    ['bandelette urinaire', 'BU'],
    ['ponction lombaire', 'PL'],
    ['liquide cérébro-spinal', 'LCS', 'LCR', 'liquide céphalo-rachidien'],
    ['abdomen sans préparation', 'ASP'],
    ['électroencéphalogramme', 'EEG'],
    ['hémoculture', 'hémocultures', 'HC'],
    ['échographie', 'écho'],
    ['angioscanner', 'angio-TDM', 'angio-scanner'],

    // --- hépato-gastro-entérologie
    ['reflux gastro-œsophagien', 'RGO'],
    ['maladie inflammatoire chronique de l\'intestin', 'MICI'],
    ['maladie de Crohn', 'MC'],
    ['rectocolite hémorragique', 'RCH'],
    ['carcinome hépatocellulaire', 'CHC'],
    ['hypertension portale', 'HTP'],
    ['endoscopie œso-gastro-duodénale', 'EOGD', 'fibroscopie', 'gastroscopie'],
    ['cholangio-pancréatographie rétrograde endoscopique', 'CPRE'],
    ['vésicule biliaire', 'VB'],
    ['insuffisance hépatocellulaire', 'IHC'],
    ['hémorragie digestive haute', 'HDH'],
    ['hémorragie digestive basse', 'HDB'],
    ['cancer colorectal', 'CCR'],
    ['hépatite alcoolique aiguë', 'HAA'],

    // --- néphrologie et urologie
    ['insuffisance rénale aiguë', 'IRA'],
    ['insuffisance rénale chronique', 'IRC'],
    ['maladie rénale chronique', 'MRC'],
    ['débit de filtration glomérulaire', 'DFG', 'clairance', 'clairance de la créatinine'],
    ['syndrome néphrotique', 'SN'],
    ['glomérulonéphrite aiguë', 'GNA'],
    ['pyélonéphrite aiguë', 'PNA', 'pyélonéphrite'],
    ['infection urinaire', 'IU', 'infection du tractus urinaire'],
    ['hypertrophie bénigne de prostate', 'HBP', 'adénome de prostate'],
    ['antigène prostatique spécifique', 'PSA'],
    ['toucher rectal', 'TR'],
    ['rétention aiguë d\'urine', 'RAU'],
    ['hémodialyse', 'HD', 'dialyse'],
    ['dialyse péritonéale', 'DP'],
    ['lithotritie extracorporelle', 'LEC'],
    ['syndrome hémolytique et urémique', 'SHU'],

    // --- endocrinologie, métabolisme, nutrition
    ['diabète de type 1', 'DT1', 'diabète type 1'],
    ['diabète de type 2', 'DT2', 'diabète type 2'],
    ['hémoglobine glyquée', 'HbA1c', 'HbA1C'],
    ['thyréostimuline', 'TSH'],
    ['thyroxine libre', 'T4L', 'T4 libre'],
    ['indice de masse corporelle', 'IMC', 'BMI'],
    ['syndrome des ovaires polykystiques', 'SOPK'],
    ['insuffisance surrénale', 'ISL', 'insuffisance surrénalienne'],
    ['hypoglycémie', 'hypoglycémies'],
    ['acidocétose diabétique', 'ACD', 'acido-cétose'],
    ['diabète gestationnel', 'DG'],
    ['dénutrition', 'malnutrition'],

    // --- neurologie
    ['sclérose en plaques', 'SEP'],
    ['sclérose latérale amyotrophique', 'SLA'],
    ['état de mal épileptique', 'EME'],
    ['hémorragie sous-arachnoïdienne', 'HSA', 'hémorragie méningée'],
    ['hématome extradural', 'HED', 'hématome épidural'],
    ['hématome sous-dural', 'HSD'],
    ['perte de connaissance', 'PC', 'PCI', 'perte de connaissance initiale'],
    ['score de Glasgow', 'GCS', 'Glasgow'],
    ['hypertension intracrânienne', 'HTIC'],
    ['maladie d\'Alzheimer', 'MA'],
    ['maladie de Parkinson', 'MP'],
    ['syndrome de Guillain-Barré', 'SGB'],
    ['myasthénie', 'myasthenia gravis'],

    // --- infectiologie
    ['antibiotique', 'ATB', 'antibiothérapie', 'ATBth', 'antibiotiques'],
    ['bacille à Gram négatif', 'BGN'],
    ['cocci à Gram positif', 'CGP'],
    ['Staphylococcus aureus résistant à la méticilline', 'SARM', 'SAMR'],
    ['bêta-lactamase à spectre étendu', 'BLSE'],
    ['virus de l\'immunodéficience humaine', 'VIH', 'HIV'],
    ['syndrome d\'immunodéficience acquise', 'SIDA'],
    ['virus de l\'hépatite B', 'VHB'],
    ['virus de l\'hépatite C', 'VHC'],
    ['infection sexuellement transmissible', 'IST', 'MST'],
    ['bacille de Koch', 'BK', 'Mycobacterium tuberculosis'],
    ['tuberculose', 'TB'],
    ['infection tuberculeuse latente', 'ITL'],
    ['cytomégalovirus', 'CMV'],
    ['virus Epstein-Barr', 'EBV'],
    ['fièvre aiguë', 'fièvre', 'hyperthermie', 'pyrexie'],
    ['sepsis', 'état septique'],
    ['choc septique', 'choc infectieux'],
    ['méningite', 'méningites'],

    // --- hématologie et hémostase
    ['hémoglobine', 'Hb'],
    ['volume globulaire moyen', 'VGM'],
    ['taux de prothrombine', 'TP'],
    ['temps de céphaline activée', 'TCA'],
    ['international normalized ratio', 'INR'],
    ['antivitamine K', 'AVK'],
    ['anticoagulant oral direct', 'AOD', 'NACO', 'anticoagulants oraux directs'],
    ['héparine de bas poids moléculaire', 'HBPM'],
    ['héparine non fractionnée', 'HNF'],
    ['leucémie aiguë myéloïde', 'LAM'],
    ['leucémie aiguë lymphoblastique', 'LAL'],
    ['leucémie lymphoïde chronique', 'LLC'],
    ['leucémie myéloïde chronique', 'LMC'],
    ['myélome multiple', 'MM'],
    ['lymphome non hodgkinien', 'LNH'],
    ['lymphome de Hodgkin', 'LH'],
    ['coagulation intravasculaire disséminée', 'CIVD'],
    ['purpura thrombopénique immunologique', 'PTI'],
    ['culot globulaire', 'CGR', 'concentré de globules rouges', 'transfusion'],

    // --- gynécologie-obstétrique
    ['semaines d\'aménorrhée', 'SA'],
    ['date des dernières règles', 'DDR'],
    ['prééclampsie', 'PE', 'pré-éclampsie', 'toxémie gravidique'],
    ['hématome rétroplacentaire', 'HRP'],
    ['retard de croissance intra-utérin', 'RCIU'],
    ['menace d\'accouchement prématuré', 'MAP'],
    ['rupture prématurée des membranes', 'RPM'],
    ['interruption volontaire de grossesse', 'IVG'],
    ['interruption médicale de grossesse', 'IMG'],
    ['fausse couche spontanée', 'FCS'],
    ['grossesse extra-utérine', 'GEU'],
    ['clarté nucale', 'CN'],
    ['trisomie 21', 'T21'],
    ['hémorragie du post-partum', 'HPP'],
    ['mort fœtale in utero', 'MFIU'],
    ['rythme cardiaque fœtal', 'RCF'],
    ['contraception œstroprogestative', 'COP', 'pilule œstroprogestative'],
    ['dispositif intra-utérin', 'DIU', 'stérilet'],
    ['human chorionic gonadotropin', 'hCG', 'bêta-hCG', 'BHCG'],

    // --- pédiatrie
    ['bronchiolite aiguë', 'bronchiolite', 'BVA'],
    ['gastro-entérite aiguë', 'GEA'],
    ['mort inattendue du nourrisson', 'MIN', 'MSN'],
    ['périmètre crânien', 'PC crânien'],
    ['nouveau-né', 'NN', 'nouveau né'],
    ['nourrisson', 'nrs'],
    ['virus respiratoire syncytial', 'VRS'],

    // --- rhumatologie, dermatologie, ophtalmologie, ORL
    ['polyarthrite rhumatoïde', 'PR'],
    ['spondyloarthrite', 'SPA', 'spondylarthrite ankylosante'],
    ['lupus érythémateux systémique', 'LES', 'lupus'],
    ['rhumatisme articulaire aigu', 'RAA'],
    ['artérite à cellules géantes', 'ACG', 'maladie de Horton', 'Horton'],
    ['pseudopolyarthrite rhizomélique', 'PPR'],
    ['carcinome basocellulaire', 'CBC'],
    ['carcinome épidermoïde', 'CE', 'carcinome spinocellulaire'],
    ['dégénérescence maculaire liée à l\'âge', 'DMLA'],
    ['baisse d\'acuité visuelle', 'BAV visuelle', 'baisse de l\'acuité visuelle'],
    ['otite moyenne aiguë', 'OMA'],
    ['angine', 'amygdalite'],

    // --- psychiatrie
    ['épisode dépressif caractérisé', 'EDC', 'dépression', 'épisode dépressif majeur', 'EDM'],
    ['tentative de suicide', 'TS'],
    ['trouble du comportement alimentaire', 'TCA', 'troubles des conduites alimentaires'],
    ['trouble obsessionnel compulsif', 'TOC'],
    ['état de stress post-traumatique', 'ESPT', 'PTSD'],
    ['trouble bipolaire', 'TB bipolaire'],

    // --- santé publique, LCA, méthodologie
    ['risque relatif', 'RR'],
    ['odds ratio', 'OR'],
    ['hazard ratio', 'HR'],
    ['intervalle de confiance', 'IC', 'IC95', 'IC à 95 %', 'intervalle de confiance à 95 %'],
    ['number needed to treat', 'NNT', 'nombre de sujets à traiter'],
    ['number needed to harm', 'NNH'],
    ['valeur prédictive positive', 'VPP'],
    ['valeur prédictive négative', 'VPN'],
    ['sensibilité', 'Se'],
    ['spécificité', 'Sp'],
    ['rapport de vraisemblance', 'RV', 'RdV', 'likelihood ratio', 'LR'],
    ['en intention de traiter', 'ITT', 'intention de traiter'],
    ['per protocole', 'PP'],
    ['essai contrôlé randomisé', 'ECR', 'RCT', 'essai randomisé'],
    ['réduction absolue du risque', 'RAR'],
    ['réduction relative du risque', 'RRR'],
    ['aire sous la courbe', 'AUC', 'ASC'],
    ['significatif', 'significative', 'statistiquement significatif'],

    // --- transversal, prise en charge, organisation
    ['antécédent', 'ATCD', 'antécédents', 'atcds'],
    ['traitement', 'ttt', 'thérapeutique', 'traitements', 'traiter', 'traité', 'traitée', 'prescrire', 'prescrit'],
    ['anomalie', 'anormal', 'anormale', 'anormaux', 'anomalies', 'anormalité'],
    ['indication', 'indiqué', 'indiquée', 'recommandé', 'recommandée', 'recommandation'],
    ['contre-indication', 'CI', 'contre-indications', 'contre indiqué'],
    ['effet indésirable', 'EI', 'effets indésirables', 'iatrogénie'],
    ['prise en charge', 'PEC'],
    ['altération de l\'état général', 'AEG'],
    ['histoire de la maladie', 'HDM'],
    ['signes fonctionnels urinaires', 'SFU'],
    ['anesthésie générale', 'AG'],
    ['anesthésie locale', 'AL'],
    ['affection de longue durée', 'ALD'],
    ['service d\'accueil des urgences', 'SAU', 'urgences'],
    ['unité de soins intensifs', 'USI', 'soins intensifs'],
    ['réanimation', 'réa', 'REA'],
    ['infirmier diplômé d\'État', 'IDE', 'infirmière', 'infirmier'],
    ['Haute Autorité de santé', 'HAS'],
    ['Agence nationale de sécurité du médicament', 'ANSM'],
    ['Organisation mondiale de la santé', 'OMS'],
    ['éducation thérapeutique du patient', 'ETP', 'éducation thérapeutique'],
    ['voie intraveineuse', 'IV', 'intraveineux', 'intraveineuse'],
    ['voie intramusculaire', 'IM voie', 'intramusculaire'],
    ['voie sous-cutanée', 'SC', 'sous-cutané', 'sous-cutanée'],
    ['per os', 'PO', 'voie orale', 'par voie orale'],
    ['corticoïde', 'CTC', 'corticoïdes', 'corticothérapie', 'glucocorticoïde'],
    ['anti-inflammatoire non stéroïdien', 'AINS'],
    ['inhibiteur de l\'enzyme de conversion', 'IEC'],
    ['antagoniste des récepteurs de l\'angiotensine 2', 'ARA2', 'sartan', 'ARAII'],
    ['inhibiteur de la pompe à protons', 'IPP'],
    ['inhibiteur sélectif de la recapture de la sérotonine', 'ISRS'],
    ['imagerie', 'examen d\'imagerie'],
    ['hospitalisation', 'hospitaliser', 'hospitalisé'],
    ['surveillance', 'surveiller', 'monitorage'],
    ['dépistage', 'dépister'],
    ['pronostic', 'pronostique'],
    ['diagnostic', 'dg', 'diag', 'diagnostique'],
    ['diagnostic différentiel', 'DD', 'diagnostics différentiels'],
    ['facteur de risque', 'FDR', 'facteurs de risque'],
    ['évolution', 'évolutif'],
    ['complication', 'complications', 'compliqué'],
    ['récidive', 'rechute', 'récurrence'],
    ['mortalité', 'létalité', 'décès', 'mort'],
    ['prévalence'], ['incidence'],
    ['urgence', 'urgent', 'en urgence'],
    ['systématique', 'systématiquement'],
    ['bilan', 'bilans'],

    // --- symptômes et signes (formes savantes et courantes)
    ['dyspnée', 'essoufflement', 'difficulté respiratoire'],
    ['asthénie', 'fatigue'],
    ['céphalée', 'céphalées', 'mal de tête', 'maux de tête'],
    ['myalgie', 'myalgies', 'douleur musculaire'],
    ['arthralgie', 'arthralgies', 'douleur articulaire'],
    ['prurit', 'démangeaison', 'démangeaisons'],
    ['ictère', 'jaunisse'],
    ['hématémèse', 'vomissement de sang'],
    ['méléna', 'selles noires'],
    ['rectorragie', 'rectorragies', 'sang rouge par l\'anus'],
    ['dysurie'], ['pollakiurie'], ['polyurie'], ['oligurie'], ['anurie'], ['hématurie'],
    ['amaigrissement', 'perte de poids'],
    ['anorexie', 'perte d\'appétit'],
    ['vomissement', 'vomissements'],
    ['diarrhée', 'diarrhées'],
    ['constipation'],
    ['douleur thoracique', 'douleurs thoraciques'],
    ['douleur abdominale', 'douleurs abdominales'],
    ['palpitation', 'palpitations'],
    ['syncope', 'malaise', 'lipothymie'],
    ['œdème', 'oedeme', 'œdèmes'],
    ['adénopathie', 'adénopathies', 'ganglion'],
    ['splénomégalie', 'grosse rate'],
    ['hépatomégalie', 'gros foie'],
    ['anémie', 'anémique'],
    ['thrombopénie', 'thrombocytopénie'],
    ['leucopénie', 'neutropénie'],
    ['hyperleucocytose', 'leucocytose'],
    ['insuffisance respiratoire aiguë', 'IRespA', 'détresse respiratoire'],
    ['tachycardie'], ['bradycardie'], ['hypotension'], ['polypnée', 'tachypnée'],
    ['confusion', 'syndrome confusionnel'], ['convulsion', 'convulsions', 'crise convulsive'],
    ['déshydratation'],
];

// Mots vides : articles, pronoms, verbes creux de la prose médicale. Ils apparaissent dans presque tous les
// points d'une fiche, donc ils ne distinguent rien — les compter gonflerait artificiellement les scores.
export const VIDES = ('le la les un une des du de d au aux à a en et ou ni or car mais donc que qui quoi dont où '
    + 'ce ces cet cette son sa ses leur leurs il elle ils elles on nous vous se sur sous dans par pour avec sans '
    + 'chez vers entre depuis pendant après avant plus moins très trop peu bien aussi même autre autres tout tous '
    + 'toute toutes aucun aucune chaque si non oui est sont etre est-ce fait faire faut il-faut peut peuvent '
    + 'doit doivent avoir ont a-t-il sera seront serait etait etaient y-a-t-il '
    + 'cas patient patiente patients malade sujet sujets type forme niveau lors suite fois facon maniere '
    + 'presence absence associe associee associes present presente existe retrouve retrouvee note notee '
    + 'realise realisee effectue effectuee permet permettent repose reposent base basee comprend comporte '
    + 'rapporte rapportee rapportes constate constatee signale signalee decrit decrite survient apparait '
    + 'correspond concerne represente constitue montre objective recherche rechercher trouve observe '
    + 'general generale generalement souvent parfois rarement toujours jamais notamment surtout principalement '
    + 'important importante essentiel essentielle possible necessaire indique indiquee '
    + 'apres avant pendant selon environ ainsi alors puis ensuite enfin egalement '
    + 'nombre taux valeur seuil critere criteres element elements signe signes '
    + 'clinique cliniques biologique biologiques '
    + 'x2 etc cf voir').split(/\s+/);

// Équivalences numériques usuelles aux EDN. Une conversion générique (mg ↔ g) est calculée ; ces paires-là
// dépendent de la molécule et ne se devinent pas, elles sont donc écrites.
export const EQUIV_VALEURS = [
    [{ v: 1.26, u: 'g/l' }, { v: 7, u: 'mmol/l' }],       // glycémie à jeun, seuil du diabète
    [{ v: 2, u: 'g/l' }, { v: 11.1, u: 'mmol/l' }],       // glycémie, seuil sur prélèvement aléatoire
    [{ v: 1.1, u: 'g/l' }, { v: 6.1, u: 'mmol/l' }],      // hyperglycémie modérée à jeun
    [{ v: 0.92, u: 'g/l' }, { v: 5.1, u: 'mmol/l' }],     // diabète gestationnel, à jeun
    [{ v: 1.5, u: 'g/l' }, { v: 3.9, u: 'mmol/l' }],      // LDL
    [{ v: 1, u: 'g/l' }, { v: 2.6, u: 'mmol/l' }],        // LDL
];

// Unités ramenées à une forme unique, avec le facteur vers l'unité de base de leur famille.
export const UNITES = {
    g: ['masse', 1], gramme: ['masse', 1], grammes: ['masse', 1],
    mg: ['masse', 0.001], milligramme: ['masse', 0.001], milligrammes: ['masse', 0.001],
    ug: ['masse', 0.000001], mcg: ['masse', 0.000001], microgramme: ['masse', 0.000001],
    kg: ['masse', 1000], kilogramme: ['masse', 1000], kilogrammes: ['masse', 1000],
    l: ['volume', 1], litre: ['volume', 1], litres: ['volume', 1],
    ml: ['volume', 0.001], millilitre: ['volume', 0.001], cc: ['volume', 0.001],
    'g/l': ['concmasse', 1], 'mg/l': ['concmasse', 0.001], 'ug/l': ['concmasse', 0.000001],
    'mg/dl': ['concmasse', 0.01], 'g/dl': ['concmasse', 10],
    'mmol/l': ['concmol', 1], 'umol/l': ['concmol', 0.001], 'mol/l': ['concmol', 1000],
    'mmhg': ['pression', 1], 'cmh2o': ['pression', 0.735],
    min: ['temps', 1], minute: ['temps', 1], minutes: ['temps', 1],
    h: ['temps', 60], heure: ['temps', 60], heures: ['temps', 60],
    j: ['temps', 1440], jour: ['temps', 1440], jours: ['temps', 1440],
    semaine: ['temps', 10080], semaines: ['temps', 10080], sem: ['temps', 10080],
    mois: ['temps', 43200],
    an: ['temps', 525600], ans: ['temps', 525600], annee: ['temps', 525600], annees: ['temps', 525600],
    mm: ['longueur', 1], cm: ['longueur', 10], m: ['longueur', 1000],
    '%': ['pourcent', 1], pourcent: ['pourcent', 1],
    sa: ['sa', 1],            // semaines d'aménorrhée : jamais converties en semaines calendaires
    ui: ['ui', 1], mui: ['ui', 0.001],
};
